/**
 * @spec docs/specs/features/staking-position-ui.md#phase-2-authentication
 * Combined authentication state hook
 */
import { useCallback, useState } from 'react';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { isSocialAuth, hasEmbeddedWallet } from '@/lib/auth';

export interface UseAuthReturn {
  /** Whether user is authenticated (via any method) */
  isAuthenticated: boolean;
  /** Whether user has completed social auth (email, Google, passkey) */
  hasSocialAuth: boolean;
  /** Whether user has a Privy embedded wallet */
  hasEmbeddedWallet: boolean;
  /** Whether user only has wallet connection (no social auth) */
  isWalletOnlyMode: boolean;
  /** User's email if logged in via email */
  email: string | null;
  /** User's primary wallet address */
  walletAddress: string | null;
  /** Whether authentication is loading */
  isLoading: boolean;
  /** Open sign-in modal */
  login: () => void;
  /** Log out */
  logout: () => Promise<void>;
  /** Get access token for authenticated requests */
  getAccessToken: () => Promise<string | null>;
  /** Open upgrade prompt (wallet-only → social auth) */
  showUpgradePrompt: boolean;
  setShowUpgradePrompt: (show: boolean) => void;
  /** Check if action requires auth, show prompt if not authenticated */
  requireAuth: () => boolean;
}

/**
 * Hook for managing authentication state and actions
 *
 * Provides unified access to:
 * - Authentication status (social vs wallet-only)
 * - User info (email, wallet address)
 * - Auth actions (login, logout)
 * - Access token for API requests
 * - Upgrade prompt management
 */
export function useAuth(): UseAuthReturn {
  const {
    authenticated,
    ready,
    user,
    login,
    logout: privyLogout,
    getAccessToken: privyGetAccessToken,
  } = usePrivy();
  const { wallets } = useWallets();

  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  // Check auth types
  const hasSocialAuthStatus = isSocialAuth(user);
  const hasEmbeddedWalletStatus = hasEmbeddedWallet(user);
  const isWalletOnlyMode = authenticated && !hasSocialAuthStatus;

  // Get user email
  const email = user?.email?.address ?? null;

  // Get primary wallet address (prefer embedded wallet)
  const embeddedWallet = wallets.find((w) => w.walletClientType === 'privy');
  const externalWallet = wallets.find((w) => w.walletClientType !== 'privy');
  const walletAddress = embeddedWallet?.address ?? externalWallet?.address ?? null;

  // Logout wrapper
  const logout = useCallback(async () => {
    await privyLogout();
  }, [privyLogout]);

  // Get access token
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!authenticated) return null;
    try {
      return await privyGetAccessToken();
    } catch {
      return null;
    }
  }, [authenticated, privyGetAccessToken]);

  // Require auth for transaction - returns true if authenticated, shows prompt if not
  const requireAuth = useCallback((): boolean => {
    if (!authenticated) {
      setShowUpgradePrompt(true);
      return false;
    }
    return true;
  }, [authenticated]);

  return {
    isAuthenticated: authenticated,
    hasSocialAuth: hasSocialAuthStatus,
    hasEmbeddedWallet: hasEmbeddedWalletStatus,
    isWalletOnlyMode,
    email,
    walletAddress,
    isLoading: !ready,
    login,
    logout,
    getAccessToken,
    showUpgradePrompt,
    setShowUpgradePrompt,
    requireAuth,
  };
}
